import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  FlowContent,
  FlowPatientOverview,
  FlowSummary,
  FlowSurface,
  FlowTimelineItem,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  assignFlowToPatient,
  cancelPatientFlow,
  fetchFlow,
  fetchPatientFlows,
  listFlows,
  pausePatientFlow,
  removePatientFlowItem,
  resumePatientFlow,
} from "@/lib/flows-api";
import {
  FlowCalendarGrid,
  buildMonthMatrix,
  paletteColor,
  parseISODate,
  sameDay,
  toISODate,
  type CalendarCell,
} from "@/components/FlowCalendarGrid";
import { ViewToggle } from "@/components/ViewToggle";
import { CalendarLegend } from "@/components/CalendarLegend";
import { flowContentTitle } from "@/lib/flow-content";
import { useReadOnly } from "./SupportView";

// TES-166 — the per-patient flow timeline on the clinician patient page. Shows
// the patient's active flows (assign / cancel) and the upcoming materialized
// items (merged, day-grouped), with per-item removal. Backed by the TES-164
// employee-api endpoints.

const SURFACE_LABELS: Record<FlowSurface, string> = {
  home_greeting: "Hem: Hälsning",
  home_now: "Hem: Just nu",
  home_for_you: "Hem: För dig idag",
  home_weekly_checkin: "Hem: Veckoincheckning",
  home_wellness_preview: "Hem: Din plan",
  // TES-866 — named apart from "Hem: För dig idag" above on purpose: canon
  // says the question section must NOT be that strip, and two similar labels
  // in one picker is how an author places it on the wrong one.
  home_questions: "Hem: Frågesektion",
  task_list: "Stegslista",
  lab_results: "Provsvar",
  health_history: "Hälsohistorik",
  wellness_program: "Wellnessprogram",
  feedback_card: "Feedback-kort",
  coach_chat: "Coach-chatt",
  doctor_chat: "Läkarchatt",
  push: "Push",
  sms: "SMS",
  email: "E-post",
  home_card: "Hemkort",
};

function itemTitle(item: FlowTimelineItem): string {
  return flowContentTitle(item.content, item.moduleType) || "(utan titel)";
}

function dayLabel(dueOn: string): string {
  return dueOn; // ISO date (YYYY-MM-DD)
}

export function PatientFlows({ patientId }: { patientId: string }) {
  const { signOut } = useAuth();
  // TES-1040 — the admin's read-only view shows the flows and the calendar;
  // assigning, pausing, cancelling and removing items are hidden.
  const readOnly = useReadOnly();
  const [overview, setOverview] = useState<FlowPatientOverview | null>(null);
  const [published, setPublished] = useState<FlowSummary[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<"list" | "calendar">("calendar");
  const [monthCursor, setMonthCursor] = useState<Date>(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  // True once the displayed month is "decided" — either auto-snapped to the
  // first upcoming step on initial load, or moved by the clinician. After that
  // the month never moves on its own, so a later reload (assign/cancel) can't
  // yank the view out from under the clinician.
  const monthDecided = useRef(false);
  const pinMonth = useCallback((d: Date) => {
    monthDecided.current = true;
    setMonthCursor(new Date(d.getFullYear(), d.getMonth(), 1));
  }, []);

  const handleError = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    },
    [signOut],
  );

  const reload = useCallback(() => {
    fetchPatientFlows(patientId).then(setOverview).catch(handleError);
  }, [patientId, handleError]);

  useEffect(() => {
    reload();
    listFlows()
      .then((rows) =>
        setPublished(rows.filter((r) => r.status === "published")),
      )
      .catch(handleError);
  }, [reload, handleError]);

  // Auto-snap the calendar to the month of the earliest upcoming step — but only
  // once, the first time data with a timeline arrives. The server returns a
  // ~30-day forward window, so near a month boundary the steps live next month.
  useEffect(() => {
    if (monthDecided.current) return;
    const first = overview?.timeline[0]?.dueOn;
    if (first) pinMonth(parseISODate(first));
  }, [overview, pinMonth]);

  // TES-288 — pre-assign preview: fetch the published content and show what
  // will activate (modules, surfaces, day span, approval needs) + capture a
  // required reason BEFORE anything touches the patient.
  const [preview, setPreview] = useState<{
    key: string;
    name: string;
    content: FlowContent;
  } | null>(null);
  const [reason, setReason] = useState("");

  async function onOpenPreview() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const view = await fetchFlow(selected);
      if (!view.published) {
        throw new Error("Flödet saknar publicerad version.");
      }
      const name = published.find((f) => f.key === selected)?.name ?? selected;
      setReason("");
      setPreview({ key: selected, name, content: view.published });
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  }

  async function onConfirmAssign() {
    if (!preview || !reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      // expectedVersion pins the assign to the exact published version the
      // clinician just reviewed — the server 409s if it was superseded.
      await assignFlowToPatient(
        patientId,
        preview.key,
        reason.trim(),
        preview.content.version,
      );
      setPreview(null);
      setSelected("");
      setReason("");
      reload();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("flow_assignment_paused")) {
        setPreview(null);
        setError(
          "Flödet är pausat för patienten — återuppta det i listan i stället för att tilldela igen.",
        );
        reload();
      } else if (msg.includes("stale_flow_preview")) {
        setPreview(null);
        setError(
          "Flödet har publicerats i en ny version sedan förhandsvisningen — öppna förhandsvisningen igen.",
        );
      } else {
        handleError(e);
      }
    } finally {
      setBusy(false);
    }
  }

  async function onPauseResume(assignmentId: string, current: string) {
    setBusy(true);
    setError(null);
    try {
      if (current === "paused")
        await resumePatientFlow(patientId, assignmentId);
      else await pausePatientFlow(patientId, assignmentId);
      reload();
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  }

  async function onCancel(assignmentId: string) {
    setBusy(true);
    setError(null);
    try {
      await cancelPatientFlow(patientId, assignmentId);
      reload();
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  }

  async function onRemoveItem(itemId: string) {
    setBusy(true);
    setError(null);
    try {
      await removePatientFlowItem(patientId, itemId);
      reload();
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  }

  // Stable color per flow. Seeded from the active assignments first (so the
  // legend order is stable), then from any timeline source flow not in that list
  // (e.g. steps from a since-cancelled assignment still inside the window) so
  // every distinct flow gets its own color instead of colliding on a fallback.
  const colorByFlowKey = useMemo(() => {
    const map = new Map<string, string>();
    if (view !== "calendar") return map; // colors are only read in calendar view
    const add = (key: string) => {
      if (!map.has(key)) map.set(key, paletteColor(map.size));
    };
    for (const a of overview?.assignments ?? []) add(a.flowKey);
    for (const day of overview?.timeline ?? []) {
      for (const item of day.items) add(item.sourceFlowKey);
    }
    return map;
  }, [view, overview]);
  const colorForFlow = useCallback(
    (flowKey: string) =>
      colorByFlowKey.get(flowKey) ?? paletteColor(colorByFlowKey.size),
    [colorByFlowKey],
  );

  // Month grid: place each timeline item on its concrete dueOn date. The server
  // returns a ~30-day upcoming window, so months outside it render empty. Skipped
  // in the default list view so a data refresh doesn't build a grid no one sees.
  const calendarWeeks = useMemo<CalendarCell[][]>(() => {
    if (view !== "calendar") return [];
    const itemsByDate = new Map<string, FlowTimelineItem[]>();
    for (const day of overview?.timeline ?? []) {
      const iso = day.dueOn.slice(0, 10);
      itemsByDate.set(iso, day.items);
    }
    const today = new Date();
    const matrix = buildMonthMatrix(
      monthCursor.getFullYear(),
      monthCursor.getMonth(),
    );
    return matrix.map((week) =>
      week.map(({ date, inMonth }) => {
        const iso = toISODate(date);
        const items = itemsByDate.get(iso) ?? [];
        return {
          key: iso,
          label: String(date.getDate()),
          muted: !inMonth,
          highlight: sameDay(date, today),
          chips: items.map((item) => ({
            id: item.id,
            label: itemTitle(item),
            time: item.timeOfDay,
            color: colorForFlow(item.sourceFlowKey),
            meta: item.sourceFlowName,
          })),
        } satisfies CalendarCell;
      }),
    );
  }, [view, overview, monthCursor, colorForFlow]);

  const monthLabel = monthCursor.toLocaleDateString("sv-SE", {
    month: "long",
    year: "numeric",
  });

  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg">Flöden</h2>
        <div className="flex items-center gap-sm">
          <ViewToggle value={view} onChange={setView} />
          {readOnly ? null : (
            <>
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
              >
                <option value="">Välj flöde…</option>
                {published.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={onOpenPreview}
                disabled={!selected || busy}
                className="px-md py-xs rounded-md bg-brand text-white text-sm hover:opacity-90 disabled:opacity-50"
              >
                Tilldela
              </button>
            </>
          )}
        </div>
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-sm text-sm">
          {error}
        </div>
      ) : null}

      {/* Active assignments */}
      {overview && overview.assignments.length > 0 ? (
        <div className="flex flex-col gap-xs">
          <h3 className="text-xs font-semibold text-textSecondary uppercase tracking-wide">
            Tilldelade flöden
          </h3>
          {overview.assignments.map((a) => (
            <div
              key={a.id}
              className="flex items-start justify-between border-b border-surfaceMuted last:border-b-0 py-xs text-sm gap-sm"
            >
              <div className="flex flex-col gap-[2px] min-w-0">
                <span>
                  {a.flowName}{" "}
                  <span className="text-textSecondary text-xs">
                    (v{a.flowVersion} •{" "}
                    {a.source === "manual" ? "manuell" : "händelse"})
                  </span>
                  {a.status === "paused" ? (
                    <span className="ml-xs inline-block rounded-full bg-warning/15 text-warning text-[11px] font-semibold px-sm py-[1px] align-middle">
                      Pausad
                    </span>
                  ) : null}
                </span>
                {/* TES-288: who started it and why. */}
                {a.assignedByName || a.reason ? (
                  <span className="text-xs text-textSecondary truncate">
                    {a.assignedByName ? `Startad av ${a.assignedByName}` : null}
                    {a.assignedByName && a.reason ? " — " : null}
                    {a.reason ? `”${a.reason}”` : null}
                  </span>
                ) : null}
              </div>
              {readOnly ? null : (
                <div className="flex items-center gap-sm shrink-0">
                  <button
                    type="button"
                    onClick={() => onPauseResume(a.id, a.status)}
                    disabled={busy}
                    className="text-xs text-textSecondary hover:text-textPrimary disabled:opacity-50"
                  >
                    {a.status === "paused" ? "Återuppta" : "Pausa"}
                  </button>
                  <button
                    type="button"
                    onClick={() => onCancel(a.id)}
                    disabled={busy}
                    className="text-xs text-textSecondary hover:text-danger disabled:opacity-50"
                  >
                    Avbryt
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : null}

      {/* Upcoming — list or calendar */}
      <div className="flex flex-col gap-sm">
        <div className="flex items-center justify-between gap-sm">
          <h3 className="text-xs font-semibold text-textSecondary uppercase tracking-wide">
            {view === "list"
              ? "Kommande (30 dagar)"
              : monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)}
          </h3>
          {view === "calendar" ? (
            <div className="flex items-center gap-xs text-xs">
              <button
                type="button"
                onClick={() =>
                  pinMonth(
                    new Date(
                      monthCursor.getFullYear(),
                      monthCursor.getMonth() - 1,
                      1,
                    ),
                  )
                }
                className="px-sm py-xs rounded-md border border-surfaceMuted text-textSecondary hover:text-textPrimary"
                aria-label="Föregående månad"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={() => pinMonth(new Date())}
                className="px-sm py-xs rounded-md border border-surfaceMuted text-textSecondary hover:text-textPrimary"
              >
                Idag
              </button>
              <button
                type="button"
                onClick={() =>
                  pinMonth(
                    new Date(
                      monthCursor.getFullYear(),
                      monthCursor.getMonth() + 1,
                      1,
                    ),
                  )
                }
                className="px-sm py-xs rounded-md border border-surfaceMuted text-textSecondary hover:text-textPrimary"
                aria-label="Nästa månad"
              >
                ›
              </button>
            </div>
          ) : null}
        </div>

        {view === "calendar" && !overview ? (
          <p className="text-textSecondary text-sm">Hämtar…</p>
        ) : view === "calendar" ? (
          <div className="flex flex-col gap-sm">
            <FlowCalendarGrid weeks={calendarWeeks} showWeekdays />
            <CalendarLegend
              items={(overview?.assignments ?? []).map((a) => ({
                key: a.id,
                color: colorForFlow(a.flowKey),
                label: a.flowName,
              }))}
            />
            <p className="text-[10px] text-textSecondary">
              Visar materialiserade steg i serverns fönster (~30 dagar framåt).
            </p>
          </div>
        ) : !overview ? (
          <p className="text-textSecondary text-sm">Hämtar…</p>
        ) : overview.timeline.length === 0 ? (
          <p className="text-textSecondary text-sm">
            Inga kommande steg. Tilldela ett flöde för att börja.
          </p>
        ) : (
          overview.timeline.map((day) => (
            <div key={day.dueOn} className="flex flex-col gap-xs">
              <div className="text-xs font-medium text-textPrimary">
                {dayLabel(day.dueOn)}
              </div>
              {day.items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-md border border-surfaceMuted rounded-md px-sm py-xs text-sm"
                >
                  <div className="flex items-center gap-sm min-w-0">
                    {item.timeOfDay ? (
                      <span className="text-textSecondary text-xs tabular-nums">
                        {item.timeOfDay}
                      </span>
                    ) : null}
                    <span className="truncate">{itemTitle(item)}</span>
                    <span className="flex gap-xs shrink-0">
                      {item.surfaces.map((s) => (
                        <span
                          key={s}
                          className="px-xs py-[1px] rounded-pill text-[10px] font-medium bg-surfaceMuted text-textSecondary"
                        >
                          {SURFACE_LABELS[s]}
                        </span>
                      ))}
                    </span>
                  </div>
                  <div className="flex items-center gap-sm shrink-0">
                    <span className="text-textSecondary text-xs hidden sm:inline">
                      {item.sourceFlowName}
                    </span>
                    {readOnly ? null : (
                      <button
                        type="button"
                        onClick={() => onRemoveItem(item.id)}
                        disabled={busy}
                        title="Ta bort"
                        className="text-xs text-textSecondary hover:text-danger disabled:opacity-50"
                      >
                        Ta bort
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* TES-288 — pre-assign confirmation: exactly what activates, where it
          shows, for how long, and whether anything needs approval — plus a
          required reason, captured before the patient is touched. */}
      {preview ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-md">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="assign-preview-title"
            className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-md bg-surface border border-surfaceMuted p-md flex flex-col gap-sm"
          >
            <h3 id="assign-preview-title" className="font-display text-lg">
              Tilldela ”{preview.name}”?
            </h3>
            {(() => {
              const items = preview.content.items;
              const daySpan =
                items.length > 0
                  ? Math.max(...items.map((i) => i.dayOffset)) + 1
                  : 0;
              const surfaces = [
                ...new Set(items.flatMap((i) => i.surfaces)),
              ] as FlowSurface[];
              const needsApproval = items.some(
                (i) => i.moduleType === "chat_message",
              );
              return (
                <>
                  <p className="text-sm text-textSecondary">
                    {items.length} steg över {daySpan}{" "}
                    {daySpan === 1 ? "dag" : "dagar"}
                    {surfaces.length > 0
                      ? ` • visas som: ${surfaces
                          .map((s) => SURFACE_LABELS[s])
                          .join(", ")}`
                      : null}
                    {needsApproval
                      ? " • innehåller chattmeddelanden som kräver godkännande innan patienten ser dem"
                      : null}
                  </p>
                  <div className="flex flex-col gap-[2px] max-h-52 overflow-y-auto border border-surfaceMuted rounded-md p-sm">
                    {items.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between text-xs py-[2px]"
                      >
                        <span className="truncate">
                          {flowContentTitle(item.content, item.moduleType) ||
                            "(utan titel)"}
                        </span>
                        <span className="text-textSecondary shrink-0 ml-sm">
                          dag {item.dayOffset}
                          {item.moduleType === "chat_message"
                            ? " • kräver godkännande"
                            : null}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
            <label className="flex flex-col gap-xs text-sm">
              <span className="text-xs font-semibold text-textSecondary uppercase tracking-wide">
                Varför startas flödet? (visas i patientens journalspår)
              </span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="T.ex. Patienten rapporterar ökad stress vid veckoincheckning v.27"
                className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
              />
            </label>
            <div className="flex items-center justify-end gap-sm">
              <button
                type="button"
                onClick={() => setPreview(null)}
                disabled={busy}
                className="px-md py-xs rounded-md border border-surfaceMuted text-sm text-textSecondary hover:text-textPrimary disabled:opacity-50"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={onConfirmAssign}
                disabled={busy || !reason.trim()}
                className="px-md py-xs rounded-md bg-brand text-white text-sm hover:opacity-90 disabled:opacity-50"
              >
                Tilldela flödet
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
